import {Tabs} from 'platform/components';
import {Box, Show} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {useCallback, useRef, useState} from 'react';

import {isNotNil, not} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import type {Money} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {PendingChangesBlocker} from '../../../../../../components/PendingChangesBlocker/PendingChangesBlocker';
import {FinancingPanel} from './FinancingPanel';
import {LeasingPanel} from './LeasingPanel';
import {FinancingSection} from './types/FinancingSection';
import {PendingEditor} from './types/PendingEditor';
import {applySavingSectionChange, type SavingSectionCounts} from './utils/applySavingSectionChange';
import {savePendingEditors} from './utils/savePendingEditors';

const SECTIONS = ['FINANCING', 'LEASING'] as const;

const NO_SAVES_IN_FLIGHT: SavingSectionCounts = {FINANCING: 0, LEASING: 0};

interface FinancingAndLeasingEditorProps {
  saleVehicleId: string;
  currency: string;
  vehiclePrice: Money | null;
  activeSection: FinancingSection;
  onSectionChange: (section: FinancingSection) => void;
  canReadFinancing: boolean;
  canCreateFinancing: boolean;
  canUpdateFinancing: boolean;
  canDeleteFinancing: boolean;
  canReadTemplates: boolean;
  canReadLeasing: boolean;
  canCreateLeasing: boolean;
  canUpdateLeasing: boolean;
  canDeleteLeasing: boolean;
  hasFinancing: boolean;
  hasLeasing: boolean;
}

export function FinancingAndLeasingEditor(props: FinancingAndLeasingEditorProps) {
  // Each panel publishes one editor object with a stable identity, but the objects themselves are
  // held in a ref: only the "is anything unsaved" transition is state, so a keystroke in either
  // panel never re-renders this component (and with it the other panel and both matrices).
  const pendingEditorsRef = useRef<Partial<Record<FinancingSection, PendingEditor>>>({});
  const [hasPendingEditors, setHasPendingEditors] = useState(false);
  const [savingSections, setSavingSections] = useState<SavingSectionCounts>(NO_SAVES_IN_FLIGHT);
  const collectPendingEditors = () =>
    SECTIONS.flatMap((editorSection) => {
      const editor = pendingEditorsRef.current[editorSection];

      return isNotNil(editor) ? [editor] : [];
    });
  const updatePendingEditor = (editorSection: FinancingSection, editor: PendingEditor | null) => {
    const nextEditors = {...pendingEditorsRef.current};

    match(editor)
      .with(Pattern.nonNullable, (pendingEditor) => {
        nextEditors[editorSection] = pendingEditor;
      })
      .otherwise(() => {
        delete nextEditors[editorSection];
      });

    pendingEditorsRef.current = nextEditors;
    setHasPendingEditors(SECTIONS.some((section) => isNotNil(nextEditors[section])));
  };
  // Each section reports its saves as a boolean, but several of its producers can save at once, so
  // the reports are reference-counted instead of overwriting one shared flag.
  const updateSavingSection = (editorSection: FinancingSection, isSaving: boolean) => {
    setSavingSections((currentSections) =>
      applySavingSectionChange(currentSections, editorSection, isSaving)
    );
  };
  const isSavingAnySection = Object.values(savingSections).some((count) => count > 0);
  // react-router's `useBlocker` re-registers the blocker whenever the predicate identity
  // changes, so the predicate must stay stable while nothing about the saving state changes.
  const shouldBlockNavigation = useCallback(
    // A save in progress must not pop the unsaved-changes dialog — the pending editors are
    // cleared only once the save resolves.
    () => not(isSavingAnySection),
    [isSavingAnySection]
  );

  return (
    <>
      <Box
        data-testid="vehicle-financing-and-leasing-tabs"
        backgroundColor="palettes.white.10.100"
        borderRadius="small"
        boxShadow="elevation_1"
      >
        <Tabs
          variant="container"
          isLazy
          lazyBehavior="keepMounted"
          isFullWidth
          activeTabId={props.activeSection}
          onChange={(value) => props.onSectionChange(value === 'LEASING' ? 'LEASING' : 'FINANCING')}
          tabs={[
            {
              id: 'FINANCING',
              title: i18n.t('entity.financing.labels.pageTitle'),
              isDisabled: !props.canReadFinancing,
              badge: props.hasFinancing ? {icon: 'toggle/tick', colorScheme: 'green'} : undefined,
              content: (
                <Show when={props.canReadFinancing && isNotNilOrEmpty(props.saleVehicleId)}>
                  <FinancingPanel
                    saleVehicleId={props.saleVehicleId}
                    currency={props.currency}
                    canReadTemplates={props.canReadTemplates}
                    isCreateAllowed={props.canCreateFinancing}
                    isUpdateAllowed={props.canUpdateFinancing}
                    isDeleteAllowed={props.canDeleteFinancing}
                    onPendingChange={(editor) => updatePendingEditor('FINANCING', editor)}
                    onSavingChange={(isSaving) => updateSavingSection('FINANCING', isSaving)}
                    onValidationError={() => props.onSectionChange('FINANCING')}
                  />
                </Show>
              ),
            },
            {
              id: 'LEASING',
              title: i18n.t('entity.vehicle.labels.leasing'),
              isDisabled: !props.canReadLeasing,
              badge: props.hasLeasing ? {icon: 'toggle/tick', colorScheme: 'green'} : undefined,
              content: (
                <Show when={props.canReadLeasing && isNotNilOrEmpty(props.saleVehicleId)}>
                  <LeasingPanel
                    saleVehicleId={props.saleVehicleId}
                    currency={props.currency}
                    vehiclePrice={props.vehiclePrice}
                    isCreateAllowed={props.canCreateLeasing}
                    isUpdateAllowed={props.canUpdateLeasing}
                    isDeleteAllowed={props.canDeleteLeasing}
                    onPendingChange={(editor) => updatePendingEditor('LEASING', editor)}
                    onSavingChange={(isSaving) => updateSavingSection('LEASING', isSaving)}
                    onValidationError={() => props.onSectionChange('LEASING')}
                  />
                </Show>
              ),
            },
          ]}
        />
      </Box>
      <PendingChangesBlocker
        isDirty={hasPendingEditors}
        isSaving={isSavingAnySection}
        shouldBlockNavigation={shouldBlockNavigation}
        onSave={() => savePendingEditors(collectPendingEditors())}
      />
    </>
  );
}
